import { jsx as _jsx } from "react/jsx-runtime";
import { eventsThemeColors } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { Span } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerOnboardingTopPromoBannerView } from "@faire/web-api--source/events/retailerOnboarding/view/topPromoBanner";
import insiderMarketingPage from "@faire/web-api--source/routes/user/insider/info";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";
import { CountdownBanner } from "@faire/retailer/components/CountdownBanner/CountdownBanner";
import { PromoBannerType } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { popSignUp, SignUpElement, SignUpType } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { MarketStore, MarketState, RetailerSegment } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
// displayed when the user is logged out
var renderLoggedOutView = function(_lastDay, mainEventRangeDate, history) {
    var handleSignUp = function() {
        return popSignUp(history, SignUpType.GENERIC, function() {
            return null;
        }, {
            element: SignUpElement.TOP_BANNER,
            sourceId: "market_countdown_banner"
        });
    };
    if (MarketStore.get().currentStateIncludingLoggedOut === MarketState.MAIN_EVENT) {
        // Main event
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "eOpDMR",
            defaultMessage: [
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Shop now!"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " Get up to 30% off at "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: " through "
                },
                {
                    type: 1,
                    value: "eventEndDate"
                },
                {
                    type: 0,
                    value: ". "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Sign up to shop"
                        }
                    ]
                }
            ],
            values: {
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                },
                marketEventName: MarketStore.get().marketName,
                eventEndDate: MarketStore.get().formattedLastDay,
                LinkFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(Link, {
                        onClick: handleSignUp,
                        children: chunks
                    });
                }
            }
        });
    } else if (MarketStore.get().currentStateIncludingLoggedOut === MarketState.EARLY_ACCESS) {
        // Early access day
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "miUrwK",
            defaultMessage: [
                {
                    type: 0,
                    value: "Get up to 30% off at "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: " this week! "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Sign up to shop"
                        }
                    ]
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                LinkFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(Link, {
                        onClick: handleSignUp,
                        children: chunks
                    });
                },
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                }
            }
        });
    }
    // Pre-market
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "gJcuBO",
        defaultMessage: [
            {
                type: 0,
                value: "Get up to 30% off at "
            },
            {
                type: 1,
                value: "marketEventName"
            },
            {
                type: 0,
                value: ", "
            },
            {
                type: 1,
                value: "mainEventRangeDate"
            },
            {
                type: 0,
                value: "! "
            },
            {
                type: 8,
                value: "LinkFormatting",
                children: [
                    {
                        type: 0,
                        value: "Sign up to shop"
                    }
                ]
            }
        ],
        "data-test-id": "countdown-pre-market-logged-out",
        values: {
            marketEventName: MarketStore.get().marketName,
            mainEventRangeDate: mainEventRangeDate,
            LinkFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(Link, {
                    onClick: handleSignUp,
                    children: chunks
                });
            },
            MediumFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(MediumText, {
                    children: chunks
                });
            }
        }
    });
};
// displayed when there is >= 24 hours before the start or end of the event
var renderDaysBeforeDeadlineView = function(daysBeforeNumber, timeRemainingTime, mainEventRangeDate) {
    var _MarketStore_get = MarketStore.get(), retailerSegment = _MarketStore_get.retailerSegment, showAllSpecials = _MarketStore_get.showAllSpecials;
    if (!MarketStore.get().isLiveForCountdownBanner) {
        if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
            // Pre-market. Enrolled
            return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "vuIvuI",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Just "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "daysBeforeNumber"
                            },
                            {
                                type: 0,
                                value: " "
                            },
                            {
                                type: 6,
                                value: "daysBeforeNumber",
                                options: {
                                    one: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "day"
                                            }
                                        ]
                                    },
                                    other: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "days"
                                            }
                                        ]
                                    }
                                },
                                offset: 0,
                                pluralType: "cardinal"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " and "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "! "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Start planning"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    daysBeforeNumber: daysBeforeNumber,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: MarketStore.get().marketSpecialsRoute,
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "c/fwL9",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Just "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "daysBeforeNumber"
                            },
                            {
                                type: 0,
                                value: " "
                            },
                            {
                                type: 6,
                                value: "daysBeforeNumber",
                                options: {
                                    one: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "day"
                                            }
                                        ]
                                    },
                                    other: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "days"
                                            }
                                        ]
                                    }
                                },
                                offset: 0,
                                pluralType: "cardinal"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " and "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "!"
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    daysBeforeNumber: daysBeforeNumber,
                    timeRemainingTime: timeRemainingTime,
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
            // Pre-market. Eligible
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "9G9saF",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Just "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "daysBeforeNumber"
                            },
                            {
                                type: 0,
                                value: " "
                            },
                            {
                                type: 6,
                                value: "daysBeforeNumber",
                                options: {
                                    one: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "day"
                                            }
                                        ]
                                    },
                                    other: {
                                        value: [
                                            {
                                                type: 0,
                                                value: "days"
                                            }
                                        ]
                                    }
                                },
                                offset: 0,
                                pluralType: "cardinal"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " and "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "! "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Become an Insider"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    daysBeforeNumber: daysBeforeNumber,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: insiderMarketingPage(),
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        }
        // Pre-market. Non-enrolled
        return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "JgeUo+",
            defaultMessage: [
                {
                    type: 0,
                    value: "Get up to 30% off at "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: ", "
                },
                {
                    type: 1,
                    value: "mainEventRangeDate"
                },
                {
                    type: 0,
                    value: "! "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Start planning"
                        }
                    ]
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                mainEventRangeDate: mainEventRangeDate,
                LinkFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(Link, {
                        linkTo: MarketStore.get().marketSpecialsRoute,
                        children: chunks
                    });
                }
            }
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "p3Uqdm",
            defaultMessage: [
                {
                    type: 0,
                    value: "Get up to 30% off at "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: ", "
                },
                {
                    type: 1,
                    value: "mainEventRangeDate"
                },
                {
                    type: 0,
                    value: "!"
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                mainEventRangeDate: mainEventRangeDate
            }
        });
    }
    // During market
    return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "xZzTVD",
        defaultMessage: [
            {
                type: 0,
                value: "Just "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "daysBeforeNumber"
                    },
                    {
                        type: 0,
                        value: " "
                    },
                    {
                        type: 6,
                        value: "daysBeforeNumber",
                        options: {
                            one: {
                                value: [
                                    {
                                        type: 0,
                                        value: "day"
                                    }
                                ]
                            },
                            other: {
                                value: [
                                    {
                                        type: 0,
                                        value: "days"
                                    }
                                ]
                            }
                        },
                        offset: 0,
                        pluralType: "cardinal"
                    }
                ]
            },
            {
                type: 0,
                value: " and "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "timeRemainingTime"
                    }
                ]
            },
            {
                type: 0,
                value: " left to shop the best deals at "
            },
            {
                type: 1,
                value: "marketEventName"
            },
            {
                type: 0,
                value: ". "
            },
            {
                type: 8,
                value: "LinkFormatting",
                children: [
                    {
                        type: 0,
                        value: "Shop now"
                    }
                ]
            }
        ],
        values: {
            marketEventName: MarketStore.get().marketName,
            daysBeforeNumber: daysBeforeNumber,
            timeRemainingTime: timeRemainingTime,
            LinkFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(Link, {
                    linkTo: MarketStore.get().marketSpecialsRoute,
                    children: chunks
                });
            },
            MediumFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(MediumText, {
                    children: chunks
                });
            }
        }
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "TqCI90",
        defaultMessage: [
            {
                type: 0,
                value: "Just "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "daysBeforeNumber"
                    },
                    {
                        type: 0,
                        value: " "
                    },
                    {
                        type: 6,
                        value: "daysBeforeNumber",
                        options: {
                            one: {
                                value: [
                                    {
                                        type: 0,
                                        value: "day"
                                    }
                                ]
                            },
                            other: {
                                value: [
                                    {
                                        type: 0,
                                        value: "days"
                                    }
                                ]
                            }
                        },
                        offset: 0,
                        pluralType: "cardinal"
                    }
                ]
            },
            {
                type: 0,
                value: " and "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "timeRemainingTime"
                    }
                ]
            },
            {
                type: 0,
                value: " left to shop the best deals at "
            },
            {
                type: 1,
                value: "marketEventName"
            },
            {
                type: 0,
                value: "."
            }
        ],
        values: {
            marketEventName: MarketStore.get().marketName,
            daysBeforeNumber: daysBeforeNumber,
            timeRemainingTime: timeRemainingTime,
            MediumFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(MediumText, {
                    children: chunks
                });
            }
        }
    });
};
// displayed when there is < 24 hours before the start or end of the event
var renderHoursBeforeDeadlineView = function(timeRemainingTime) {
    var _MarketStore_get = MarketStore.get(), retailerSegment = _MarketStore_get.retailerSegment, showAllSpecials = _MarketStore_get.showAllSpecials;
    if (timeRemainingTime < "00:00:00") {
        return null;
    }
    if (MarketStore.get().isEarlyAccessDay) {
        if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
            // Early access day. Enrolled Insider
            return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "Hw/gqF",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Hey Insider! Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " left for exclusive early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: ". "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Shop now"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: MarketStore.get().marketSpecialsRoute,
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "R+miez",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Hey Insider! Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " left for exclusive early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "."
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
            // Early access day. Eligible
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "YXk5tt",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "! Join Insider to start shopping now. "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Become an Insider"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: insiderMarketingPage(),
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        }
        // Early access day. Non-eligible
        return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "e9c0Yz",
            defaultMessage: [
                {
                    type: 0,
                    value: "Only "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 1,
                            value: "timeRemainingTime"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " until "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: " opens! "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Start planning"
                        }
                    ]
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                timeRemainingTime: timeRemainingTime,
                LinkFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(Link, {
                        linkTo: MarketStore.get().marketSpecialsRoute,
                        children: chunks
                    });
                },
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                }
            }
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "SODBXf",
            defaultMessage: [
                {
                    type: 0,
                    value: "Only "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 1,
                            value: "timeRemainingTime"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " until "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: " opens!"
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                timeRemainingTime: timeRemainingTime,
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                }
            }
        });
    }
    if (!MarketStore.get().isLiveForCountdownBanner) {
        if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
            // Pre-market. Enrolled
            return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "L9HOuc",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "! "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Start planning"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: MarketStore.get().marketSpecialsRoute,
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "ehb81P",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "!"
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
            // Pre-market. Eligible
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "VlArXr",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Only "
                    },
                    {
                        type: 8,
                        value: "MediumFormatting",
                        children: [
                            {
                                type: 1,
                                value: "timeRemainingTime"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " until Insiders get early access to "
                    },
                    {
                        type: 1,
                        value: "marketEventName"
                    },
                    {
                        type: 0,
                        value: "! "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Become an Insider"
                            }
                        ]
                    }
                ],
                values: {
                    marketEventName: MarketStore.get().marketName,
                    timeRemainingTime: timeRemainingTime,
                    LinkFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(Link, {
                            linkTo: insiderMarketingPage(),
                            children: chunks
                        });
                    },
                    MediumFormatting: function(chunks) {
                        return /*#__PURE__*/ _jsx(MediumText, {
                            children: chunks
                        });
                    }
                }
            });
        }
        // Pre-market. Non-enrolled
        return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "Ow6L/p",
            defaultMessage: [
                {
                    type: 0,
                    value: "Only "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 1,
                            value: "timeRemainingTime"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " until "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: "! "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Start planning"
                        }
                    ]
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                timeRemainingTime: timeRemainingTime,
                LinkFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(Link, {
                        linkTo: MarketStore.get().marketSpecialsRoute,
                        children: chunks
                    });
                },
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                }
            }
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "K7cKBM",
            defaultMessage: [
                {
                    type: 0,
                    value: "Only "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 1,
                            value: "timeRemainingTime"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " until "
                },
                {
                    type: 1,
                    value: "marketEventName"
                },
                {
                    type: 0,
                    value: "!"
                }
            ],
            values: {
                marketEventName: MarketStore.get().marketName,
                timeRemainingTime: timeRemainingTime,
                MediumFormatting: function(chunks) {
                    return /*#__PURE__*/ _jsx(MediumText, {
                        children: chunks
                    });
                }
            }
        });
    }
    // During market
    return showAllSpecials ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "XiuMR9",
        defaultMessage: [
            {
                type: 0,
                value: "Just "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "timeRemainingTime"
                    }
                ]
            },
            {
                type: 0,
                value: " left to shop the best deals at "
            },
            {
                type: 1,
                value: "marketEventName"
            },
            {
                type: 0,
                value: ". "
            },
            {
                type: 8,
                value: "LinkFormatting",
                children: [
                    {
                        type: 0,
                        value: "Shop now"
                    }
                ]
            }
        ],
        values: {
            marketEventName: MarketStore.get().marketName,
            timeRemainingTime: timeRemainingTime,
            LinkFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(Link, {
                    linkTo: MarketStore.get().marketSpecialsRoute,
                    children: chunks
                });
            },
            MediumFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(MediumText, {
                    children: chunks
                });
            }
        }
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "VOkC6U",
        defaultMessage: [
            {
                type: 0,
                value: "Just "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "timeRemainingTime"
                    }
                ]
            },
            {
                type: 0,
                value: " left to shop the best deals at "
            },
            {
                type: 1,
                value: "marketEventName"
            },
            {
                type: 0,
                value: "."
            }
        ],
        values: {
            marketEventName: MarketStore.get().marketName,
            timeRemainingTime: timeRemainingTime,
            MediumFormatting: function(chunks) {
                return /*#__PURE__*/ _jsx(MediumText, {
                    children: chunks
                });
            }
        }
    });
};
var MarketCountdownBannerBase = function() {
    useEffect(function() {
        // Special case banner that is not part of the normal banner rotation
        trackRetailerOnboardingTopPromoBannerView(PromoBannerType.MARKET_COUNTDOWN);
    }, []);
    return /*#__PURE__*/ _jsx(CountdownBanner, {
        textColor: eventsThemeColors.faireMarket.promoText,
        renderDaysBeforeDeadlineView: renderDaysBeforeDeadlineView,
        renderHoursBeforeDeadlineView: renderHoursBeforeDeadlineView,
        renderLoggedOutView: renderLoggedOutView,
        centerAlignIcon: true
    });
};
export var MarketCountdownBanner = observer(MarketCountdownBannerBase);
var Link = styled(EmbeddedLink).attrs({
    color: eventsThemeColors.faireMarket.promoText
}).withConfig({
    componentId: "sc-e397ca81-0"
})([
    ""
]);
var MediumText = styled(Span).attrs({
    variant: "paragraphSansMedium",
    color: eventsThemeColors.faireMarket.promoText
}).withConfig({
    componentId: "sc-e397ca81-1"
})([
    ""
]);
